import * as React from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../core/i18n'
import { IJsonResponse } from '../../../core/JsonApiSerializer'
import { agentService, reservationService, utilService } from '../../../core/services'
import { Button, Buttons, InputText, ListItem, Panel } from '../../atoms'
import * as interfaces from '../../../static/interfaces'
import WithLabel from 'components/atoms/WithLabel'
import Typography from 'components/atoms/_layouts/Typography'
import ReservationCancelModalBody from '../../organisms/Reservations/ReservationCancelModalBody'
import {
  BREAKPOINT_TABLET_LARGE,
  BREAKPOINT_TABLET_SMALL,
  COLORS,
  BREAKPOINT_TABLET_MOBILE,
} from 'static/constants'
import LinkButton from 'components/atoms/LinkButton'
import SidebarLayout from '../_layouts/sidebarLayout'
import { showDeclineModal } from './parts/DeclineModal'
import injectGoogleMaps from 'utils/injectGoogleMaps'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow

interface IProps {
  reservation: IJsonResponse
  post: IJsonResponse
  user: IJsonResponse
  is_token_access?: boolean
  guestDetail: {
    tel: string
    fullname: string
    country: string
    email: string
  }
  host_cancel_reasons: any[]
  tour_reservation_id: number
  google: any
}

interface IErrors {
  [key: string]: string | null
}

interface IPickupInputProps {
  map: any
  marker: any
  coordinate: {
    pickup_latitude: number
    pickup_longitude: number
  }
  setCoordinate: any
  setPickupLocation: any
  reservation: any
  google: any
}

const getPostType = (postType: string) => {
  if (postType === 'private') {
    return I18n.locale === 'ja' ? 'プライベート' : 'Private'
  }
  return I18n.locale === 'ja' ? 'グループ' : 'Group'
}

// ページ内部にあるマップ表示との競合を避けるためにpropsで扱う
// 基本実装はReservationPickupLocationと同様
const PickupInput = ({
  map,
  marker,
  coordinate,
  setCoordinate,
  setPickupLocation,
  reservation,
  google,
}: IPickupInputProps) => {
  const [changingPlace, setChangingPlace] = React.useState(false)

  React.useEffect(() => {
    const { pickup_latitude, pickup_longitude } = coordinate

    if (google) {
      createAutoComplete()
    }

    if (google && pickup_latitude && pickup_longitude) {
      const latLng = new google.maps.LatLng(pickup_latitude, pickup_longitude)

      if (map) {
        map.panTo(latLng)
        map.setZoom(15)
      }

      if (marker) {
        marker.setPosition(latLng)
      }
    }
  }, [google, marker, map, coordinate])
  const input = document.getElementById('pickup_location_search') as HTMLInputElement
  let autocomplete = null

  const createAutoComplete = () => {
    if (!input) {
      return
    }

    autocomplete = new google.maps.places.Autocomplete(input)

    const onPlaceChanged = () => {
      if (!autocomplete) {
        return
      }

      const place = autocomplete.getPlace()
      setCoordinate({
        pickup_latitude: place?.geometry?.location.lat() ?? null,
        pickup_longitude: place?.geometry?.location.lng() ?? null,
      })

      setPickupLocation(input.value)
      setChangingPlace(false)
    }

    autocomplete.addListener('place_changed', onPlaceChanged)
  }

  const onBlur = React.useCallback(() => {
    if (changingPlace) {
      autocomplete?.unbindAll()
      setCoordinate({
        pickup_latitude: null,
        pickup_longitude: null,
      })

      setPickupLocation(input.value)
      setChangingPlace(false)
    }
  }, [autocomplete, changingPlace])

  return (
    <AutoComplete>
      <input
        id="pickup_location_search"
        type="text"
        name="pickup_location"
        defaultValue={reservation.pickup_location}
        onChange={() => setChangingPlace(true)}
        onBlur={onBlur}
      />
    </AutoComplete>
  )
}

const Show: React.FC<IProps> = props => {
  const { data: _reservation } = reservationService.getDataFromJson(props.reservation)
  const { data: currentUser } = reservationService.getDataFromJson(props.user)
  const { data: post } = reservationService.getDataFromJson(props.post)
  const { data: cancelReasons } = props.host_cancel_reasons
    ? reservationService.getDataFromJson(props.host_cancel_reasons)
    : { data: { cancelReasons: [] } }
  const [map, setMap] = React.useState(null)
  const [marker, setMarker] = React.useState(null)
  const [coordinate, setCoordinate] = React.useState({
    pickup_latitude: _reservation.pickup_latitude ?? null,
    pickup_longitude: _reservation.pickup_longitude ?? null,
  })
  const [pickupLocation, setPickupLocation] = React.useState(_reservation.pickup_location)
  const [reservation, setReservation] = React.useState<any>(_reservation)
  const [hostCancelReason, setHostCancelReason] = React.useState<string | null>(null)
  const [directReservationUserEmail, setDirectReservationUserEmail] = React.useState<string | null>(
    reservation.guest_email ?? ''
  )
  const isCancellationComplete = React.useMemo(() => {
    const requestedAsComplete =
      new URLSearchParams(window.location.search).get('cancellation_complete') === '1'
    return requestedAsComplete && reservation.workflow_state_enum_value === 'canceled'
  }, [reservation.workflow_state_enum_value])

  const isDirectReservation = React.useMemo(() => {
    return _reservation.no.indexOf('DRE') >= 0
  }, [_reservation])

  // 直接予約のゲストのメールアドレスを変更可能かどうか
  const reservationEmailEditable = React.useMemo(() => {
    return currentUser.agent && isDirectReservation
  }, [currentUser.agent, isDirectReservation])

  // 滞在先の住所を変更可能かどうか
  const pickupLocationEditable = React.useMemo(() => {
    return (
      !reservation.is_past && post.post_type === 'private' && post.allow_pickup && currentUser.agent
    )
  }, [reservation.is_past, post.post_type, post.allow_pickup, currentUser.agent])

  const onChangeDirectReservationUserEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDirectReservationUserEmail(e.target.value)
  }
  const handleSubmitGuestEmails = async () => {
    if (isDirectReservation) {
      const updateParam = {
        id: reservation.id,
        guest_email: directReservationUserEmail,
      }

      try {
        const { updatedReservation, flush } = await agentService.updateReservation(
          updateParam,
          false
        )
        setReservation(updatedReservation)
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      } catch (e) {
        console.error(e)
      }
    }
  }

  const formRef = React.useRef(null)

  const initializeMap = () => {
    const { pickup_latitude, pickup_longitude } = coordinate

    if (!pickup_latitude || !pickup_longitude) {
      return
    }

    const LatLng = new props.google.maps.LatLng(pickup_latitude, pickup_longitude)
    const Options = {
      zoom: 15,
      center: LatLng,
      mapTypeId: 'roadmap',
      scrollwheel: false,
      draggable: false,
    }
    const initializedMap = new props.google.maps.Map(document.getElementById('map'), Options)
    const initializedMarker = new props.google.maps.Marker({
      position: LatLng,
      title: post.name,
      map: initializedMap,
    })

    setMap(initializedMap)
    setMarker(initializedMarker)
  }

  React.useEffect(() => {
    const isNeedToInitializeMap = !map && coordinate.pickup_latitude && coordinate.pickup_longitude
    if (props.google && isNeedToInitializeMap) {
      initializeMap()
    }
  }, [props.google, map, coordinate.pickup_latitude, coordinate.pickup_longitude])

  React.useEffect(() => {
    const set = async () => {
      if (reservation && reservation.workflow_state_enum_value === 'declined') {
        const selectedReason = cancelReasons.find(
          reason => reason.id === reservation.host_cancel_reason
        )
        setHostCancelReason(
          I18n.locale === 'ja' ? selectedReason?.reason_ja : selectedReason?.reason
        )
      }
    }
    set()
  }, [reservation])

  const messagePath =
    reservation.conversation && [post.user_id, reservation.user.id].includes(currentUser.id)
      ? `/conversations/${reservation.conversation.slug}`
      : null

  const approveReservation = React.useCallback(async id => {
    const { reservation: changedReservation, flush } = await reservationService.approveReservation(
      id
    )
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    location.href = `/agent/host_reservations`
  }, [])

  const repayReservation = React.useCallback(async id => {
    if (!window.confirm('この予約を再決済します。よろしいですか？')) {
      return
    }
    try {
      const {
        reservation: changedReservation,
        flush,
        client_secret,
      } = await reservationService.repayReservation(id)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.reload()
    } catch (error) {
      console.error(error)
    }
  }, [])

  const showCancelModal = (id, workflowState) => {
    window.globalModal.showModal({
      title: I18n.t('cancel.cancel_reservation'),
      body: <ReservationCancelModalBody reservationId={id} reservationStatus={workflowState} />,
    })
  }
  const handleDecline = (_, __, flush) => {
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    location.href = `/agent/host_reservations`
  }

  const renderWorkFlowStateButtons = (
    { id, workflow_state_enum_value, is_past, next_work_flow_state },
    isOwner
  ) => {
    if (next_work_flow_state?.includes('repay')) {
      return (
        <Buttons inline={true}>
          <Button
            key={`repay`}
            small={true}
            primary={true}
            handleClick={() => repayReservation(reservation.id)}
            rounded={true}
          >
            {I18n.t('generic.repay')}
          </Button>
        </Buttons>
      )
    } else if (is_past) {
      return null
    } else if (isOwner && workflow_state_enum_value !== 'requested') {
      return null
    } else if (!isOwner && !['requested', 'approved'].includes(workflow_state_enum_value)) {
      return null
    }
    return (
      <Buttons inline={true}>
        {isOwner ? (
          <>
            <Button
              key={`approve`}
              small={true}
              handleClick={() => approveReservation(reservation.id)}
              rounded={true}
            >
              {I18n.t('generic.approve')}
            </Button>
            <Button
              key={`decline`}
              textColor={COLORS.caution}
              borderColor={COLORS.caution}
              small={true}
              rounded={true}
              handleClick={() => {
                showDeclineModal(currentUser, reservation.id, handleDecline, formRef, cancelReasons)
              }}
            >
              {I18n.t('generic.decline')}
            </Button>
          </>
        ) : (
          <Button
            handleClick={() => showCancelModal(id, workflow_state_enum_value)}
            textColor={COLORS.caution}
            borderColor={COLORS.caution}
            small={true}
            rounded={true}
          >
            {I18n.t('cancel.cancel_reservation')}
          </Button>
        )}
      </Buttons>
    )
  }

  const canUpdatePickup = React.useMemo(() => {
    const hasCoordinate =
      coordinate.pickup_latitude !== null && coordinate.pickup_longitude !== null

    return pickupLocation !== '' && hasCoordinate
  }, [pickupLocation, coordinate.pickup_latitude, coordinate.pickup_longitude])

  const handleSubmitPickupLocation = async () => {
    if (!canUpdatePickup) {
      return
    }

    const updateParam = {
      id: reservation.id,
      pickup_location: pickupLocation,
      pickup_latitude: coordinate.pickup_latitude,
      pickup_longitude: coordinate.pickup_longitude,
    }

    const { updatedReservation, flush } = await agentService.updateReservation(
      updateParam,
      !isDirectReservation
    )
    setReservation(updatedReservation)
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
  }

  return (
    <SidebarLayout user={currentUser}>
      <Panel noDecoration={true}>
        {!props.is_token_access && (
          <BackButton>
            <LinkButton
              href={`${currentUser.guest ? '/reservations' : '/agent/host_reservations'}`}
            >
              <>
                <SvgIcon
                  name="keyboard_arrow_left"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                {I18n.t('generic.back_to_index')}
              </>
            </LinkButton>
          </BackButton>
        )}
        <S.Reservation>
          <div className="title">
            <Typography type="headline" size="md">
              {isCancellationComplete ? 'Cancellation Complete' : I18n.t('reservation.detail')}
            </Typography>
            {isCancellationComplete && (
              <div className="cancellation-complete-message">
                <SvgIcon
                  name="check_circle"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                The following reservation has been canceled.
              </div>
            )}
          </div>
          <Panel noDecoration={true}>
            <ListItem>
              <WithLabel label={I18n.t('reservation.reservation_no')}>{reservation.no}</WithLabel>
            </ListItem>
            <ListItem>
              <WithLabel label={I18n.t('post.post_name')}>{post.name}</WithLabel>
            </ListItem>
            <ListItem>
              <WithLabel label={I18n.t('reservation.reservation_datetime')}>
                <Typography>{reservation.created_at}</Typography>
              </WithLabel>
            </ListItem>
            <ListItem>
              <WithLabel label={I18n.t('generic.tour_schedule')}>
                <Typography>
                  {reservation.start_at} - {reservation.end_at}
                </Typography>
              </WithLabel>
            </ListItem>
            <ListItem>
              <WithLabel label={I18n.t('generic.status')}>
                <Typography>{reservation.workflow_state}</Typography>
              </WithLabel>
            </ListItem>
            {reservation.workflow_state_enum_value === 'declined' && hostCancelReason && (
              <>
                <ListItem>
                  <WithLabel label={I18n.t('cancel.cancel_reason')}>
                    <Typography>{hostCancelReason}</Typography>
                  </WithLabel>
                </ListItem>
                <ListItem>
                  <WithLabel label="Cancellation requested by guest">
                    <Typography>
                      {reservation.cancellation_requested_by_guest ? 'YES' : 'NO'}
                    </Typography>
                  </WithLabel>
                </ListItem>
                <ListItem>
                  <WithLabel label="Canceled by">
                    <Typography>{reservation.canceled_by}</Typography>
                  </WithLabel>
                </ListItem>
              </>
            )}
            {reservation.workflow_state_enum_value === 'canceled' && (
              <ListItem>
                <WithLabel label={I18n.t('cancel.cancel_reason')}>
                  <Typography>{reservation.cancel_reason}</Typography>
                </WithLabel>
              </ListItem>
            )}
            <ListItem>
              <WithLabel
                label={
                  currentUser.guest ? I18n.t('generic.guide_name') : I18n.t('generic.guest_name')
                }
              >
                <Typography>
                  {currentUser.guest
                    ? post.user.username
                    : reservation.user
                    ? `${props.guestDetail?.fullname}(${reservation.user?.username})`
                    : reservation.guest_name}
                </Typography>
              </WithLabel>
            </ListItem>
            <ListItem>
              <WithLabel label={I18n.t('reservation.number_of_guests')}>
                <div className="person">
                  <div className="nb_of">
                    <Typography type="subtitle" size="lg">
                      {I18n.t('generic.adult')}
                    </Typography>
                    <Typography type="subtitle" size="lg" color="subtext">
                      {I18n.t('generic.nb_of_person', { nb: reservation.num_of_adults })}
                    </Typography>
                  </div>
                  <div className="nb_of">
                    <Typography type="subtitle" size="lg">
                      {I18n.t('generic.child')}
                    </Typography>
                    <Typography type="subtitle" size="lg" color="subtext">
                      {I18n.t('generic.nb_of_person', { nb: reservation.num_of_children })}
                    </Typography>
                  </div>
                  <div className="nb_of">
                    <Typography type="subtitle" size="lg">
                      {I18n.t('generic.infant')}
                    </Typography>
                    <Typography type="subtitle" size="lg" color="subtext">
                      {I18n.t('generic.nb_of_person', { nb: reservation.num_of_infants })}
                    </Typography>
                  </div>
                </div>
              </WithLabel>
            </ListItem>
            <ListItem>
              <WithLabel label={I18n.t('generic.country')}>
                <Typography>{props.guestDetail?.country}</Typography>
              </WithLabel>
            </ListItem>
            <ListItem>
              <WithLabel label={I18n.t('generic.tel')}>
                <Typography>{props.guestDetail?.tel}</Typography>
              </WithLabel>
            </ListItem>
            <ListItem>
              <WithLabel label={I18n.t('generic.email')}>
                {reservationEmailEditable && !reservation.is_past ? (
                  <InputText
                    name="to"
                    placeholder="sample@example.com"
                    value={reservation.guest_email}
                    onChangeHandler={onChangeDirectReservationUserEmail}
                  />
                ) : (
                  <Typography>
                    {props.guestDetail?.email ?? reservation.guest_email ?? '-'}
                  </Typography>
                )}
              </WithLabel>
            </ListItem>
            {reservationEmailEditable && !reservation.is_past && (
              <ListItem>
                <Button handleClick={handleSubmitGuestEmails}>{I18n.t('generic.save')}</Button>
              </ListItem>
            )}
            <ListItem>
              <WithLabel label={I18n.t('generic.payment_amount')}>
                {utilService.formatPriceOnlyYen(
                  currentUser.guest ? reservation.charge_price : reservation.price
                )}
              </WithLabel>
            </ListItem>
            {/* {!isDirectReservation &&
              (currentUser.agent || reservation.assignment_status === 'assigned') && (
                <ListItem>
                  <WithLabel label={I18n.t('generic.assigned_price')}>
                    {reservation.assignment_status === 'assigned' ? reservation.assigned_price : ''}
                  </WithLabel>
                </ListItem>
              )} */}
            <ListItem separate={true}>
              <WithLabel label={I18n.t('post.tour_type')}>{getPostType(post.post_type)}</WithLabel>
            </ListItem>
            <ListItem>
              <WithLabel label={I18n.t('post.language')}>{post.language_i18n}</WithLabel>
            </ListItem>
            <ListItem separate={true}>
              <WithLabel label={I18n.t('reservation.pickup_location')}>
                {pickupLocationEditable ? (
                  <PickupInput
                    map={map}
                    marker={marker}
                    coordinate={coordinate}
                    setCoordinate={setCoordinate}
                    setPickupLocation={setPickupLocation}
                    reservation={reservation}
                    google={props.google}
                  />
                ) : (
                  reservation.pickup_location
                )}
              </WithLabel>
            </ListItem>
            {pickupLocationEditable && (
              <ListItem>
                <Button handleClick={handleSubmitPickupLocation} disabled={!canUpdatePickup}>
                  {I18n.t('generic.save')}
                </Button>
              </ListItem>
            )}
            <ListItem>
              <div id="map" className={`Map${canUpdatePickup ? '' : ' hide'}`} />
            </ListItem>
            {!currentUser.guest && (
              <ListItem>
                <WithLabel label={I18n.t('reservation.pickup_memo')}>
                  {reservation.pickup_memo || '-'}
                </WithLabel>
              </ListItem>
            )}
            <ListItem separate={true}>
              <WithLabel label={I18n.t('reservation.other_requests')}>
                {reservation.other_requests}
              </WithLabel>
            </ListItem>
            <div className="right">
              <Buttons buttonSize={120}>
                {!props.is_token_access && messagePath && (
                  <LinkButton href={messagePath} miniLink={true} showBorder={true}>
                    {I18n.t('generic.message')}
                  </LinkButton>
                )}
                {renderWorkFlowStateButtons(reservation, post.user_id === currentUser.id)}
              </Buttons>
            </div>
          </Panel>
        </S.Reservation>
      </Panel>
    </SidebarLayout>
  )
}
const S: { [key: string]: AnyStyledComponent } = {}

S.Reservation = styled.div`
  padding: 28px 40px;
  margin-bottom: 40px;
  border: 1px solid ${COLORS.primary};
  border-radius: 10px;
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.1);
  @media (max-width: ${BREAKPOINT_TABLET_LARGE}px) {
    padding: 20px;
  }
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    margin: 0 16px 24px;
  }
  @media (max-width: ${BREAKPOINT_TABLET_MOBILE}px) {
    padding: 8px 0;
    .Panel_Body {
      padding: 0 16px;
    }
  }
  .title {
    padding: 16px 0;
    @media (max-width: ${BREAKPOINT_TABLET_MOBILE}px) {
      padding: 12px 16px 0;
    }
    .cancellation-complete-message {
      margin-top: 16px;
      padding: 12px;
      background-color: ${COLORS.cardBG};
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 16px;
      font-weight: 700;
      border-radius: 4px;
      .icon {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        color: ${COLORS.primary};
      }
    }
  }

  .person {
    display: flex;
    align-items: center;
    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      align-items: flex-start;
      flex-direction: column;
    }
  }
  .nb_of {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-right: 20px;
  }
  .right {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
  }
  .Map {
    margin-top: 16px;
    height: 320px;
    @media (max-width: ${BREAKPOINT_TABLET_MOBILE}px) {
      height: 320px;
    }
    &.hide {
      display: none;
    }
  }
  .Panel {
    padding: 0;
  }
`
const AutoComplete = styled.div`
  > input {
    display: block;
    width: 100%;
    padding: 12px;
    border: solid 1px ${COLORS.divider};
    border-radius: 4px;
    font-size: 16px;
    transition: border 0.2s ease;
    outline: none;
    background: none;
    margin-top: 12px;
  }
`
const BackButton = styled.div`
  padding: 16px 0;
`

export default injectGoogleMaps(Show)
